import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { ApiError, apiVoid } from '@/lib/apiClient';

import { loginPhoneCode, type LoginPhoneCode } from './loginPhoneError';
import { tenantUsersQueryKey } from './useTenantUsers';

/**
 * SLICE 52 — change a tenant user's LOGIN phone.
 *
 * Mirrors the wire contract in `api/src/modules/admin/routes.ts`:
 *   PATCH /api/admin/users/:userId/phone   body { phone }
 *     204                                   — changed; the user is signed out everywhere
 *     400 { error: 'phone_invalid' }        — not a phone we can normalise to E.164
 *     409 { error: 'phone_is_sim' }         — that number is a tracker SIM
 *     409 { error: 'phone_in_use' }         — another user already logs in with it
 *     409 { error: 'phone_is_staff' }       — a staff member logs in with it
 *     404 { error: 'not_found' }            — no such tenant user
 *
 * A 204 with an empty body, so `apiVoid` — `apiJson` would call `res.json()` on
 * the empty body and turn a successful change into a parse error (the 17x defect).
 *
 * On success the tenant's user list is invalidated so the picker (and its prefill)
 * reflects the new number without a manual refetch — the same discipline the
 * provisioning mutations keep.
 */

export type EditLoginPhoneFailure = LoginPhoneCode | 'not_found' | 'unknown';

export function classifyEditLoginError(err: unknown): EditLoginPhoneFailure {
  const code = loginPhoneCode(err);
  if (code !== null) return code;
  if (err instanceof ApiError && err.status === 404) return 'not_found';
  return 'unknown';
}

export interface EditLoginPhoneVars {
  tenantId: string;
  userId: string;
  /** The number exactly as the operator typed it. The server normalises it to
   *  E.164 — this client never pre-normalises, so what the server stores is what
   *  it decided, not what the form guessed. */
  phone: string;
}

export function useEditLoginPhone(): UseMutationResult<void, Error, EditLoginPhoneVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, phone }: EditLoginPhoneVars) =>
      apiVoid(`/api/admin/users/${encodeURIComponent(userId)}/phone`, {
        method: 'PATCH',
        body: JSON.stringify({ phone }),
      }),
    onSuccess: (_void, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: tenantUsersQueryKey(tenantId) });
    },
  });
}
